import { useEffect, useState, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';
import './StatsManager.css';

const RANGES = [
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
  { label: '90d', value: '90d' },
  { label: 'All time', value: 'all' },
];

export default function StatsManager() {
  const { authenticatedFetch } = useAuth();
  const [range, setRange] = useState('30d');

  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [booking, setBooking] = useState(null);
  const [topPaths, setTopPaths] = useState(null);
  const [referrers, setReferrers] = useState(null);
  const [topContent, setTopContent] = useState(null);
  const [devices, setDevices] = useState(null);
  const [errors, setErrors] = useState({});

  const fetchJson = useCallback(async (path) => {
    const sep = path.includes('?') ? '&' : '?';
    const res = await authenticatedFetch(`${API_BASE_URL}/api/stats${path}${sep}range=${range}`);
    if (!res.ok) throw new Error(`Failed to fetch ${path}`);
    return res.json();
  }, [authenticatedFetch, range]);

  useEffect(() => {
    let active = true;
    setErrors({});
    setOverview(null);
    setTimeseries(null);
    setFunnel(null);
    setBooking(null);
    setTopPaths(null);
    setReferrers(null);
    setTopContent(null);
    setDevices(null);

    const load = (key, setter, p) => {
      p.then((d) => { if (active) setter(d); })
       .catch((e) => { if (active) setErrors((x) => ({ ...x, [key]: e.message })); });
    };

    load('overview', setOverview, fetchJson('/overview'));
    load('timeseries', setTimeseries, fetchJson('/timeseries'));
    load('funnel', setFunnel, fetchJson('/funnel'));
    load('booking', setBooking, fetchJson('/booking'));
    load('topPaths', setTopPaths, fetchJson('/top-paths?limit=10'));
    load('referrers', setReferrers, fetchJson('/referrers?limit=10'));
    load('topContent', setTopContent, fetchJson('/top-content?limit=10'));
    load('devices', setDevices, fetchJson('/devices'));

    return () => { active = false; };
  }, [fetchJson]);

  function pct(num, denom) {
    if (!denom || denom === 0) return '0%';
    return `${Math.round((num / denom) * 100)}%`;
  }

  function devicesPct(d) {
    const total = (d.mobile || 0) + (d.desktop || 0) + (d.tablet || 0);
    if (total === 0) return null;
    return {
      mobile: Math.round((d.mobile / total) * 100),
      desktop: Math.round((d.desktop / total) * 100),
      tablet: Math.round((d.tablet / total) * 100),
    };
  }

  function slugOf(path, prefix) {
    return path.startsWith(prefix) ? path.slice(prefix.length) : path;
  }

  return (
    <div className="stats-manager">
      <div className="stats-range">
        {RANGES.map((r) => (
          <button
            key={r.value}
            className={range === r.value ? 'active' : ''}
            onClick={() => setRange(r.value)}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="stats-overview">
        <div className="stats-card">
          <h4>Page Views</h4>
          <div className="stats-number">{overview ? overview.pageViews.toLocaleString() : '…'}</div>
        </div>
        <div className="stats-card">
          <h4>Unique Visitors</h4>
          <div className="stats-number">{overview ? overview.uniqueVisitors.toLocaleString() : '…'}</div>
        </div>
        <div className="stats-card">
          <h4>Views / Visitor</h4>
          <div className="stats-number">{overview ? overview.viewsPerVisitor.toFixed(1) : '…'}</div>
        </div>
        <div className="stats-card">
          <h4>Inquiries</h4>
          <div className="stats-number">{overview ? overview.inquirySubmits.toLocaleString() : '…'}</div>
        </div>
      </div>

      <div className="stats-section">
        <h3>Traffic over time</h3>
        {range === 'all' && <div className="stats-empty">Daily chart available for 7d / 30d / 90d ranges.</div>}
        {range !== 'all' && errors.timeseries && <div className="stats-empty">Couldn't load chart.</div>}
        {range !== 'all' && !errors.timeseries && !timeseries && <div className="stats-empty">Loading…</div>}
        {range !== 'all' && timeseries && timeseries.every((p) => p.views === 0) && <div className="stats-empty">No data yet.</div>}
        {range !== 'all' && timeseries && timeseries.some((p) => p.views > 0) && (
          <div className="stats-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeseries}>
                <CartesianGrid stroke="#eee" />
                <XAxis dataKey="date" fontSize={11} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="views" stroke="#b08968" dot={false} />
                <Line type="monotone" dataKey="visitors" stroke="#7a8c6e" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="stats-section">
        <h3>Inquiry funnel</h3>
        {errors.funnel && <div className="stats-empty">Couldn't load funnel.</div>}
        {!errors.funnel && !funnel && <div className="stats-empty">Loading…</div>}
        {funnel && funnel.visits === 0 && <div className="stats-empty">No data yet.</div>}
        {funnel && funnel.visits > 0 && (() => {
          const max = funnel.visits;
          const row = (label, value, denom, denomLabel) => (
            <div className="stats-funnel-row" key={label}>
              <span className="label">{label}</span>
              <span className="bar-track"><span className="bar-fill" style={{ width: `${(value / max) * 100}%` }} /></span>
              <span>
                <span className="value">{value.toLocaleString()}</span>
                {denom != null && <span className="pct">({pct(value, denom)} of {denomLabel})</span>}
              </span>
            </div>
          );
          return (
            <div>
              {row('Site visits', funnel.visits, null)}
              {row('Contact-page views', funnel.contactPageViews, funnel.visits, 'visits')}
              {row('Inquiry submits', funnel.submits, funnel.contactPageViews, 'contact views')}
            </div>
          );
        })()}
      </div>

      <div className="stats-section">
        <h3>Booking</h3>
        {errors.booking && <div className="stats-empty">Couldn't load booking stats.</div>}
        {!errors.booking && !booking && <div className="stats-empty">Loading…</div>}
        {booking && booking.visits === 0 && booking.clicks === 0 && <div className="stats-empty">No data yet.</div>}
        {booking && (booking.visits > 0 || booking.clicks > 0) && (() => {
          const max = booking.visits || booking.clicks || 1;
          const row = (label, value, denom, denomLabel) => (
            <div className="stats-funnel-row" key={label}>
              <span className="label">{label}</span>
              <span className="bar-track"><span className="bar-fill" style={{ width: `${(value / max) * 100}%` }} /></span>
              <span>
                <span className="value">{value.toLocaleString()}</span>
                {denom != null && <span className="pct">({pct(value, denom)} of {denomLabel})</span>}
              </span>
            </div>
          );
          return (
            <div>
              {row('Site visits', booking.visits, null)}
              {row('Booking clicks', booking.clicks, booking.visits, 'visits')}
              {row('/book page views', booking.bookPageViews, null)}
              {booking.sources.length > 0 && (
                <table className="stats-table" style={{ marginTop: '12px' }}><tbody>
                  {booking.sources.map((s) => (
                    <tr key={s.source}><td>{s.source}</td><td className="num">{s.clicks}</td></tr>
                  ))}
                </tbody></table>
              )}
            </div>
          );
        })()}
      </div>

      <div className="stats-two-col">
        <div className="stats-section">
          <h3>Top pages</h3>
          {errors.topPaths && <div className="stats-empty">Couldn't load top pages.</div>}
          {!errors.topPaths && !topPaths && <div className="stats-empty">Loading…</div>}
          {topPaths && topPaths.length === 0 && <div className="stats-empty">No data yet.</div>}
          {topPaths && topPaths.length > 0 && (
            <table className="stats-table"><tbody>
              {topPaths.map((p) => (
                <tr key={p.path}><td>{p.path}</td><td className="num">{p.views}</td></tr>
              ))}
            </tbody></table>
          )}
        </div>

        <div className="stats-section">
          <h3>Top referrers</h3>
          {errors.referrers && <div className="stats-empty">Couldn't load referrers.</div>}
          {!errors.referrers && !referrers && <div className="stats-empty">Loading…</div>}
          {referrers && referrers.length === 0 && <div className="stats-empty">No data yet.</div>}
          {referrers && referrers.length > 0 && (
            <table className="stats-table"><tbody>
              {referrers.map((r) => (
                <tr key={r.referrer}><td>{r.referrer}</td><td className="num">{r.views}</td></tr>
              ))}
            </tbody></table>
          )}
        </div>
      </div>

      <div className="stats-two-col">
        <div className="stats-section">
          <h3>Top services</h3>
          {errors.topContent && <div className="stats-empty">Couldn't load top services.</div>}
          {!errors.topContent && !topContent && <div className="stats-empty">Loading…</div>}
          {topContent && topContent.services.length === 0 && <div className="stats-empty">No data yet.</div>}
          {topContent && topContent.services.length > 0 && (
            <table className="stats-table"><tbody>
              {topContent.services.map((s) => (
                <tr key={s.path}><td>{slugOf(s.path, '/services/')}</td><td className="num">{s.views}</td></tr>
              ))}
            </tbody></table>
          )}
        </div>

        <div className="stats-section">
          <h3>Top shop products</h3>
          {errors.topContent && <div className="stats-empty">Couldn't load top products.</div>}
          {!errors.topContent && !topContent && <div className="stats-empty">Loading…</div>}
          {topContent && topContent.products.length === 0 && <div className="stats-empty">No data yet.</div>}
          {topContent && topContent.products.length > 0 && (
            <table className="stats-table"><tbody>
              {topContent.products.map((p) => (
                <tr key={p.path}><td>{slugOf(p.path, '/shop/products/')}</td><td className="num">{p.views}</td></tr>
              ))}
            </tbody></table>
          )}
        </div>
      </div>

      <div className="stats-section">
        <h3>Devices</h3>
        {errors.devices && <div className="stats-empty">Couldn't load device stats.</div>}
        {!errors.devices && !devices && <div className="stats-empty">Loading…</div>}
        {devices && (() => {
          const p = devicesPct(devices);
          if (!p) return <div className="stats-empty">No data yet.</div>;
          return (
            <div className="stats-devices">
              <span>Mobile {p.mobile}%</span>
              <span>Desktop {p.desktop}%</span>
              <span>Tablet {p.tablet}%</span>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
